'use client'

import { forwardRef, useEffect, useRef, useState } from 'react'

import { LuSearch, LuX } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { ChangeEvent, InputHTMLAttributes } from 'react'

type SearchInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> & {
  onSearch?: (value: string) => void
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  delay?: number
  classNames?: {
    container?: string
    input?: string
  }
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      onSearch,
      onChange,
      delay = 300,
      defaultValue = '',
      classNames,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(String(defaultValue))
    const onSearchRef = useRef(onSearch)

    useEffect(() => {
      onSearchRef.current = onSearch
    }, [onSearch])

    useEffect(() => {
      if (!onSearchRef.current) return

      const timer = setTimeout(() => onSearchRef.current?.(value), delay)
      return () => clearTimeout(timer)
    }, [delay, value])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value)
      onChange?.(event)
    }

    const handleClear = () => {
      setValue('')
      onSearch?.('')
    }

    return (
      <div
        className={cn(
          'flex h-11 items-center gap-2 rounded-field border border-transparent bg-surface-raised',
          'px-4 transition-colors duration-200 focus-within:border-point',
          classNames?.container
        )}
      >
        <LuSearch
          aria-hidden="true"
          className="size-5 shrink-0 text-description"
        />
        <input
          ref={ref}
          value={value}
          onChange={handleChange}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-c15 text-foreground outline-none',
            'placeholder:text-description/70',
            classNames?.input
          )}
          {...props}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            data-focus-appearance="fill"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-control text-description',
              'transition-colors duration-200 hover:bg-surface-strong hover:text-foreground',
              'focus:outline-none focus-visible:bg-surface-strong'
            )}
            aria-label="검색어 지우기"
          >
            <LuX aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'
